import type { AxiosResponse } from "axios"
import type { DateString, SortDirection } from "@/types/common"
import type { Checkup } from "@/types/exam"
import type {
	PregnanciesList,
	Pregnancy,
	PregnancyAmnios,
	PregnancyChorionicity,
	PregnancyConceptionType,
	PregnancyEndedReason,
	PregnancyMapSubType,
	PregnancyMapType,
	PregnancyStartType,
	PregnancyType
} from "@/types/pregnancy"
import { API_ROOT, HttpClient } from "@/api/httpClient"
import fetuses from "./fetuses"

export interface PregnanciesListQuery {
	perPage: number
	page: number
	sort?: SortDirection
	filters?: {
		/** solo gravidanze in corso */
		active?: boolean
	}
}

interface PregnanciesListParams {
	paginate: true
	per_page: number
	page: number
	active?: 1 | 0
	order?: SortDirection
}

export interface PregnancyPayload {
	/** YYYY-MM-DD */
	start_date: DateString
	start_date_type: PregnancyStartType
	type: PregnancyType
	start_weight: number
	// OPTIONAL
	conception_type?: PregnancyConceptionType
	/** YYYY-MM-DD */
	scheduled_c_section?: DateString
	high_risk_pregnancy: 1 | 0
	ultrasound_redating: number
	ended: 1 | 0
	/** YYYY-MM-DD */
	ended_date?: DateString
	ended_reason?: PregnancyEndedReason
	notes?: string
	map_type?: PregnancyMapType
	map_subtype?: PregnancyMapSubType
	amnios?: PregnancyAmnios
	chorionicity?: PregnancyChorionicity
	fetuses_to_remove?: number[]
}

function list(
	patientId: number,
	payload: PregnanciesListQuery & { filters: { active: true } }
): Promise<AxiosResponse<Pregnancy>>
function list(
	patientId: number,
	payload?: PregnanciesListQuery & { filters?: { active?: false } }
): Promise<AxiosResponse<PregnanciesList>>
function list(
	patientId: number,
	payload?: PregnanciesListQuery
): Promise<AxiosResponse<Pregnancy> | AxiosResponse<PregnanciesList>> {
	if (payload?.filters?.active) {
		return HttpClient.get<Pregnancy>(`${API_ROOT}/patients/${patientId}/pregnancies`, {
			params: { active: 1 }
		})
	}

	const params: PregnanciesListParams = {
		paginate: true,
		per_page: payload?.perPage || 50,
		page: payload?.page || 1,
		order: payload?.sort || "desc"
	}

	return HttpClient.get<PregnanciesList>(`${API_ROOT}/patients/${patientId}/pregnancies`, {
		params
	})
}

export default {
	list,
	upsert(patientId: number, pregnancyId: number | null, payload: PregnancyPayload) {
		return HttpClient.post<Pregnancy>(`${API_ROOT}/patients/${patientId}/pregnancies/${pregnancyId || ""}`, payload)
	},
	updateUltrasoundRedating(patientId: number, pregnancyId: number, ultrasound_redating: number) {
		return HttpClient.patch<Pregnancy>(`${API_ROOT}/patients/${patientId}/pregnancies/${pregnancyId}`, {
			ultrasound_redating
		})
	},
	updateTwinsType(
		patientId: number,
		pregnancyId: number,
		chorionicity: PregnancyChorionicity | null,
		amnios: PregnancyAmnios | null
	) {
		return HttpClient.patch<Pregnancy>(`${API_ROOT}/patients/${patientId}/pregnancies/${pregnancyId}`, {
			chorionicity,
			amnios
		})
	},
	details(patientId: number, pregnancyId: number) {
		return HttpClient.get<Pregnancy>(`${API_ROOT}/patients/${patientId}/pregnancies/${pregnancyId}`)
	},
	delete(patientId: number, pregnancyId: number) {
		return HttpClient.delete(`${API_ROOT}/patients/${patientId}/pregnancies/${pregnancyId}`)
	},
	checkup(patientId: number, pregnancyId: number) {
		return HttpClient.get<Checkup[]>(`${API_ROOT}/patients/${patientId}/pregnancies/${pregnancyId}/checkups`)
	},
	fetuses
}
